import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { dismissInvite } from "../lib/friends";
import { PUBLIC_GAMES } from "../lib/room";
import { useT } from "../i18n";
import GameIcon from "./GameIcon";

const MAX_AGE_MS = 15 * 60_000;

// Pops a toast whenever a friend invites us to their room.
export default function InviteListener() {
  const { profile } = useAuth();
  const { toast } = useToast();
  const t = useT();
  const navigate = useNavigate();
  const seen = useRef(new Set());
  const uid = profile?.uid;

  useEffect(() => {
    if (!uid) return;
    return onSnapshot(collection(db, "users", uid, "invites"), (snap) => {
      snap.docChanges().forEach((ch) => {
        if (ch.type !== "added" || seen.current.has(ch.doc.id)) return;
        seen.current.add(ch.doc.id);
        const inv = ch.doc.data();
        const created = inv.createdAt?.toMillis?.() ?? Date.now();
        if (Date.now() - created > MAX_AGE_MS) {
          dismissInvite(uid, ch.doc.id).catch(() => {});
          return;
        }
        toast({
          emoji: PUBLIC_GAMES[inv.game] ? <GameIcon id={inv.game} /> : "invite",
          title: t("{name} invited you!", { name: inv.fromName }),
          body: t("Join their {game} room {code}", {
            game: PUBLIC_GAMES[inv.game] ? t(PUBLIC_GAMES[inv.game].name) : t("game"),
            code: inv.roomCode,
          }),
          duration: 20000,
          action: {
            label: t("Join"),
            onClick: () => {
              dismissInvite(uid, ch.doc.id).catch(() => {});
              navigate(`/r/${inv.roomCode}`);
            },
          },
          onDismiss: () => dismissInvite(uid, ch.doc.id).catch(() => {}),
        });
      });
    });
  }, [uid, toast, navigate, t]);

  return null;
}
